import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Mail, MailOpen, Trash2, ExternalLink, Inbox } from "lucide-react";
import { toast } from "sonner";

const SUBJECT_LABELS = {
  info: "Information",
  "self-oneshot": "Self-hosted (one-shot)",
  "self-subscription": "Self-hosted + Updates",
  saas: "SaaS",
  support: "Support",
  other: "Autre",
};

export default function ContactInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/contact");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markRead = async (item, read) => {
    try {
      await api.patch(`/admin/contact/${item.id}`, { read });
      setItems((arr) => arr.map((x) => (x.id === item.id ? { ...x, read } : x)));
      if (selected?.id === item.id) setSelected({ ...selected, read });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (item) => {
    if (!confirm("Supprimer ce message ?")) return;
    try {
      await api.delete(`/admin/contact/${item.id}`);
      setItems((arr) => arr.filter((x) => x.id !== item.id));
      if (selected?.id === item.id) setSelected(null);
      toast.success("Message supprimé");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const open = (item) => {
    setSelected(item);
    if (!item.read) markRead(item, true);
  };

  const unreadCount = items.filter((i) => !i.read).length;

  return (
    <div className="space-y-6" data-testid="contact-inbox">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">// Commercial</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Boîte contact</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Messages reçus via le formulaire public {unreadCount > 0 && <span className="font-bold text-primary ml-1">({unreadCount} non lu{unreadCount > 1 ? "s" : ""})</span>}
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-4 border border-border bg-card rounded-sm overflow-hidden">
        <div className="border-r border-border max-h-[70vh] overflow-y-auto">
          {loading ? (
            <p className="p-8 text-center text-sm text-muted-foreground">Chargement…</p>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <Inbox className="h-12 w-12 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">Aucun message.</p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((it) => (
                <li
                  key={it.id}
                  onClick={() => open(it)}
                  className={`p-4 cursor-pointer hover:bg-accent/40 ${selected?.id === it.id ? "bg-accent/60" : ""} ${!it.read ? "border-l-2 border-primary" : ""}`}
                  data-testid={`contact-item-${it.id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className={`text-sm truncate ${!it.read ? "font-bold" : "font-medium"}`}>{it.name}</p>
                    <span className="text-[10px] text-muted-foreground shrink-0">
                      {new Date(it.created_at).toLocaleDateString("fr-FR")}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{it.email}</p>
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    <span className="font-medium">{SUBJECT_LABELS[it.subject] || it.subject}</span> — {it.message}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {selected ? (
            <article data-testid="contact-detail">
              <header className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-xl font-bold">{selected.name}</h2>
                  <a href={`mailto:${selected.email}`} className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                    {selected.email} <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => markRead(selected, !selected.read)}
                    className="p-2 hover:bg-accent rounded"
                    title={selected.read ? "Marquer non lu" : "Marquer lu"}
                    data-testid="toggle-read"
                  >
                    {selected.read ? <Mail className="h-4 w-4" /> : <MailOpen className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => remove(selected)}
                    className="p-2 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded"
                    title="Supprimer"
                    data-testid="delete-contact"
                  >
                    <Trash2 className="h-4 w-4 text-rose-500" />
                  </button>
                </div>
              </header>

              <div className="space-y-2 text-sm mb-6 pb-4 border-b border-border">
                <div className="grid grid-cols-[120px_1fr] gap-2 text-xs">
                  <span className="text-muted-foreground">Société</span><span>{selected.company || "—"}</span>
                  <span className="text-muted-foreground">Téléphone</span><span>{selected.phone || "—"}</span>
                  <span className="text-muted-foreground">Type</span><span>{SUBJECT_LABELS[selected.subject] || selected.subject}</span>
                  <span className="text-muted-foreground">Reçu</span><span>{new Date(selected.created_at).toLocaleString("fr-FR")}</span>
                </div>
              </div>

              <p className="text-sm whitespace-pre-wrap leading-relaxed">{selected.message}</p>

              <a
                href={`mailto:${selected.email}?subject=Re: Votre message WiLink`}
                className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium"
              >
                <Mail className="h-4 w-4" /> Répondre par email
              </a>
            </article>
          ) : (
            <div className="h-full flex items-center justify-center text-center text-muted-foreground text-sm py-20">
              Sélectionnez un message pour le lire.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
